import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    TextField,
    InputAdornment,
    Box,
    Typography,
    CircularProgress
} from '@mui/material';
import { Home } from '@mui/icons-material';
import UserService from '../services/UserService';

const NeighborhoodInput = ({ value, onChange, onValidation, disabled, required = true }) => {
    const [neighborhoods, setNeighborhoods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [touched, setTouched] = useState(false); // ✅ Suit si l'utilisateur a interagi avec le champ

    // ── Récupérer tous les quartiers au montage ──────────────────────────
    useEffect(() => {
        const fetchNeighborhoods = async () => {
            try {
                setLoading(true);
                const data = await UserService.getAllNeighborhoods();
                setNeighborhoods(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error fetching neighborhoods:', err);
                setNeighborhoods([]);
            } finally {
                setLoading(false);
            }
        };
        fetchNeighborhoods();
    }, []);

    // ── Fonction de validation ───────────────────────────────────────────
    const validate = (val) => {
        let err = '';
        if (required && (!val || !val.trim())) {
            err = 'Le quartier est requis';
        } else if (val && val.trim().length < 2) {
            err = 'Le quartier doit avoir au moins 2 caractères';
        }
        setError(err);
        if (onValidation) onValidation(err);
    };

    const handleValueChange = (newValue) => {
        // Autorise les lettres (avec accents), les CHIFFRES, les espaces, tirets et apostrophes
        const sanitized = (newValue || '').replace(/[^a-zA-Z0-9À-ÿ\s'-]/g, '');
        onChange(sanitized);

        // ✅ Si l'utilisateur a déjà quitté le champ une fois (touched),
        // on revalide en direct pour effacer l'erreur dès qu'il corrige.
        if (touched) {
            validate(sanitized);
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                Quartier {required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <Autocomplete
                freeSolo          /* ← l'utilisateur peut saisir librement */
                value={value || ''}
                onChange={(event, newValue) => {
                    handleValueChange(newValue);
                    // ✅ Déclenche la validation quand l'utilisateur choisit dans la liste
                    setTouched(true);
                    validate(newValue);
                }}
                inputValue={value || ''}
                onInputChange={(event, newInputValue) => {
                    handleValueChange(newInputValue);
                }}
                onBlur={() => {
                    // ✅ Déclenche la validation uniquement quand l'utilisateur sort du champ
                    setTouched(true);
                    validate(value);
                }}
                options={neighborhoods}
                loading={loading}
                disabled={disabled}
                filterOptions={(options, state) => {
                    const input = state.inputValue.toLowerCase().trim();
                    if (!input) return options;
                    return options.filter(opt =>
                        opt.toLowerCase().includes(input)
                    );
                }}
                noOptionsText="Aucun quartier trouvé — vous pouvez saisir le vôtre"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Saisissez votre quartier"
                        error={!!error}
                        helperText={error}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Home sx={{ color: '#153247' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                '&:hover fieldset': { borderColor: '#153247' },
                                '&.Mui-focused fieldset': { borderColor: '#153247' },
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#153247' },
                        }}
                    />
                )}
            />
        </Box>
    );
};

export default NeighborhoodInput;